import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { claimDocumentTitle, headingText, releaseDocumentTitle } from "../lib/document-title";
import { useUiLabels } from "../lib/ui-labels";

export interface PageHeaderProps {
  /**
   * Usually a translated string; a `ReactNode` because a detail screen's title is often the thing's
   * name with its status badge beside it, and splitting that into two headings would be a lie.
   */
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  eyebrow?: string;
  className?: string;
  /** Overrides `page-header` when a screen already has one and needs a second, addressable one. */
  testId?: string;
  /**
   * 1 for a section layout, 2 for a screen inside one.
   *
   * <p>The twelve sectioned layouts (`routes/reports.tsx` and friends) already put an `<h1>` on the
   * page with the section's name, so a screen underneath is *not* a first-level heading — it is the
   * second. Giving every screen an `<h1>` would have left two on the page, which is the same
   * problem UX-20 reported, upside down.
   */
  level?: 1 | 2;
  /**
   * What the browser tab says, when the visible title is not it — a title made of a number, a name
   * and a badge reads fine on screen and badly in a tab list. Defaults to the heading's own text.
   */
  documentTitle?: string;
}

/**
 * The title of a screen — the one `<h1>` on it (US-37.8, UX-20).
 *
 * <p>It used to live in `roastery-app` and be used on fifteen screens out of a hundred and
 * thirty-two. The other hundred and seventeen put their title in a `CardTitle`, which is an `<h3>`:
 * a screen-reader heard "heading level 3, Partie zielonego ziarna" under a level 1 that named the
 * whole section, with no level 2 in between, and every card below the title was another level 3
 * beside it rather than under it. Moving the component into `packages/ui` is what let the portal
 * and the operator panel have the same thing.
 */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  className,
  testId,
  level = 1,
  documentTitle,
}: PageHeaderProps) {
  const Heading = level === 2 ? "h2" : "h1";
  const heading = useRef<HTMLHeadingElement>(null);
  const { productName } = useUiLabels();
  const [key] = useState(() => Symbol("page-header"));

  // Every screen's tab said just "BeanFlow", so ten open tabs were ten identical tabs. The header
  // that names the screen names the tab too; the deepest header on screen wins (see
  // `document-title.ts`). No dependency list on purpose: the title is often a ReactNode that only
  // becomes its final text once data arrives, and reading it after each render is cheap.
  useEffect(() => {
    if (!productName) {
      return;
    }
    claimDocumentTitle(key, level, documentTitle ?? headingText(heading.current), productName);
  });
  useEffect(() => () => releaseDocumentTitle(key), [key]);

  return (
    <header
      // Wrapping rather than a fixed row (UX audit 2026-11): a detail screen with four buttons at
      // 820 px squeezed its title column to some sixty pixels — the batch number in three lines —
      // and at 390 px the buttons ran off the edge. The title claims a basis and grows; the buttons
      // drop under it when both do not fit, and wrap among themselves when one row is not enough.
      className={cn("flex flex-wrap items-end justify-between gap-x-4 gap-y-3", className)}
      data-testid={testId ?? (level === 2 ? "screen-header" : "page-header")}
    >
      <div className="min-w-0 flex-1 basis-72">
        {eyebrow ? (
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-primary/80">{eyebrow}</p>
        ) : null}
        {/* A flex row, because a detail screen's title is several things — a number, a name, a
            status badge — and as inline text they ran together ("PC-2026-0001Etiopia Guji"). */}
        <Heading
          ref={heading}
          className={cn(
            "flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold tracking-tight text-foreground",
            level === 2
              ? "text-xl md:text-[length:var(--page-title-sm,1.375rem)]"
              : "text-2xl md:text-[length:var(--page-title,1.75rem)]",
          )}
        >
          {title}
        </Heading>
        {description ? <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">{actions}</div> : null}
    </header>
  );
}

/**
 * A screen: its title, and everything under it.
 *
 * <p>One place for the gap between the header and the content, so a hundred and thirty screens do
 * not each pick their own.
 */
export function PageShell({
  className,
  children,
  ...props
}: { className?: string; children: ReactNode } & Omit<React.ComponentProps<"div">, "className" | "children">) {
  return (
    <div className={cn("flex flex-col gap-4", className)} {...props}>
      {children}
    </div>
  );
}
